'use memo';

import { useEffect, useState } from 'react';
import { AgxButton, AgxCollapsible, AgxModal, AgxSpacer } from '@urbanx/agx.ui-components2';
import { RenderedSpecialCondition, SpecialConditionData } from '@urbanx/agx.campaigns.client';
import { CustomSpecialConditionForm } from './CustomSpecialConditionForm';
import {
  createCustomSpecialConditionDrafts,
  CustomSpecialConditionDrafts,
  CustomSpecialConditionMode,
  getActiveCustomSpecialCondition,
  getNextCustomSpecialConditionClauseNumber,
  getUsedCustomSpecialConditionClauseNumbers,
  validateCustomSpecialCondition,
} from './customSpecialConditionEditor';

interface AddSpecialConditionButtonProps {
  id: string;
  label: string;
  existingConditions: SpecialConditionData[];
  renderedConditions?: RenderedSpecialCondition[];
  onAddSpecialCondition: (_specialCondition: SpecialConditionData) => boolean | Promise<boolean>;
  campaignId: string;
  buttonVariant?: 'primary' | 'hollow' | 'dark';
  mode?: 'modal' | 'inline';
  registerBeforeSave?: (handler: (() => boolean | Promise<boolean>) | undefined) => void;
}

interface ValidatedSpecialConditionAddition {
  activeCondition: SpecialConditionData;
  isAdding: boolean;
  isValid: boolean;
  onAddSpecialCondition: (specialCondition: SpecialConditionData) => boolean | Promise<boolean>;
  setIsAdding: (isAdding: boolean) => void;
  setIsOpen: (isOpen: boolean) => void;
  setTriggerValidation: (triggerValidation: boolean) => void;
}

const addValidatedSpecialCondition = async ({
  activeCondition,
  isAdding,
  isValid,
  onAddSpecialCondition,
  setIsAdding,
  setIsOpen,
  setTriggerValidation,
}: ValidatedSpecialConditionAddition) => {
  setTriggerValidation(true);

  if (!isValid || isAdding) return false;

  setIsAdding(true);
  try {
    const wasAdded = await onAddSpecialCondition(activeCondition);
    if (!wasAdded) return false;

    setIsOpen(false);
    return true;
  } finally {
    setIsAdding(false);
  }
};

export function AddSpecialConditionButton({
  id,
  label,
  existingConditions,
  renderedConditions = [],
  onAddSpecialCondition,
  campaignId,
  buttonVariant = 'hollow',
  mode = 'modal',
  registerBeforeSave,
}: AddSpecialConditionButtonProps) {
  const usedClauseNumbers = getUsedCustomSpecialConditionClauseNumbers(
    existingConditions,
    renderedConditions
  );
  const defaultClauseNumber = getNextCustomSpecialConditionClauseNumber(usedClauseNumbers);
  const [isOpen, setIsOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [triggerValidation, setTriggerValidation] = useState(false);
  const [selectedMode, setSelectedMode] =
    useState<CustomSpecialConditionMode>('without-subclauses');
  const [drafts, setDrafts] = useState<CustomSpecialConditionDrafts>(() =>
    createCustomSpecialConditionDrafts(defaultClauseNumber)
  );

  const activeCondition = getActiveCustomSpecialCondition(drafts, selectedMode);
  const validation = validateCustomSpecialCondition(activeCondition, usedClauseNumbers);

  const onAddConditionClicked = () => {
    if (mode === 'inline' && isOpen) return;

    setSelectedMode('without-subclauses');
    setDrafts(createCustomSpecialConditionDrafts(defaultClauseNumber));
    setTriggerValidation(false);
    setIsOpen(true);
  };

  useEffect(() => {
    if (mode !== 'inline' || !registerBeforeSave) return;

    if (!isOpen) {
      registerBeforeSave(undefined);
      return;
    }

    registerBeforeSave(() =>
      addValidatedSpecialCondition({
        activeCondition,
        isAdding,
        isValid: validation.isValid,
        onAddSpecialCondition,
        setIsAdding,
        setIsOpen,
        setTriggerValidation,
      })
    );

    return () => registerBeforeSave(undefined);
  }, [
    activeCondition,
    isAdding,
    isOpen,
    mode,
    onAddSpecialCondition,
    registerBeforeSave,
    validation.isValid,
  ]);

  const editor = (
    <AgxSpacer
      direction="vertical"
      padding={
        mode === 'modal'
          ? {
              top: 'spacing-3xl',
              bottom: 'spacing-3xl',
              left: 'xl',
              right: 'xl',
            }
          : 'spacing-2xl'
      }
      fill
    >
      <CustomSpecialConditionForm
        id={id}
        mode={selectedMode}
        drafts={drafts}
        usedClauseNumbers={usedClauseNumbers}
        existingConditions={existingConditions}
        campaignId={campaignId}
        triggerValidation={triggerValidation}
        onModeChange={setSelectedMode}
        onDraftsChange={setDrafts}
      />
    </AgxSpacer>
  );

  if (mode === 'inline') {
    return (
      <AgxSpacer direction="vertical" fill gap="spacing-md">
        {isOpen && (
          <AgxCollapsible
            variant="withDivider"
            open
            enableToggle
            iconPosition="left"
            title={`Special Condition ${activeCondition.overriddenClauseNumber ?? ''}`}
            content={editor}
            headerPadding={{
              top: 'spacing-xl',
              bottom: 'spacing-xl',
              left: 'spacing-2xl',
              right: 'spacing-2xl',
            }}
            isContentFit
          />
        )}
        <AgxButton
          id={`${id}-add-special-conditions`}
          variant={buttonVariant}
          text={label}
          size="medium"
          onClick={onAddConditionClicked}
          disabled={isAdding}
        />
      </AgxSpacer>
    );
  }

  return (
    <>
      <AgxButton
        id={`${id}-add-special-conditions`}
        variant={buttonVariant}
        text={label}
        size="medium"
        onClick={onAddConditionClicked}
        disabled={isAdding}
      />
      <AgxModal
        title="Add Special Condition"
        variant="withDivider"
        type="single"
        actionButtonContainerStyle="withDivider"
        maxWidth="600px"
        setIsDialogOpen={setIsOpen}
        isOpen={isOpen}
        content={editor}
        actionButtons={
          <AgxSpacer direction="horizontal" gap="sm" fill>
            <AgxButton
              id={`${id}-back-add-special-condition`}
              variant="hollow"
              text="Back"
              size="large"
              stretch
              onClick={() => setIsOpen(false)}
              disabled={isAdding}
            />
            <AgxButton
              id={`${id}-confirm-add-special-condition`}
              variant="primary"
              text="Confirm"
              size="large"
              stretch
              onClick={() =>
                void addValidatedSpecialCondition({
                  activeCondition,
                  isAdding,
                  isValid: validation.isValid,
                  onAddSpecialCondition,
                  setIsAdding,
                  setIsOpen,
                  setTriggerValidation,
                })
              }
              disabled={isAdding}
            />
          </AgxSpacer>
        }
      />
    </>
  );
}
